// Step 3 W5 — the loan's vendor program history, rendered from the audit rows
// the assignment service has been writing all along.
//
// This card used to be static copy: "Vendor swaps, terminations, and MNCA exit
// events appear on the loan's Activity tab. The full audit trail is captured by
// the backend IAuditLogger per spec §4.8." The Activity tab cannot show them —
// ActivityOwnerPermissions maps "loan" to Loans.Read so the request is
// authorized, and then ModuleActivitySourceProvider falls through to an empty
// array because it has no "loan" arm. The card pointed at a dead end, and the
// standalone audit page it alludes to is itself a placeholder.

import { Card, StatusBadge } from "@/design-system/components/ui";
import type { StatusTone } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { useLoanVendorHistory } from "@/hooks/portfolioAdmin/useVendorReports";

const KIND_LABEL: Record<string, string> = {
  assigned: "Assigned",
  updated: "Updated",
  removed: "Removed",
};

const KIND_TONE: Record<string, StatusTone> = {
  assigned: "ok",
  updated: "info",
  removed: "muted",
};

export function LoanVendorHistoryCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const { dateTime } = useDateFormatter();
  const canRead = auth.hasPermission("Vendors.Read");
  const historyQuery = useLoanVendorHistory(loanId, { enabled: canRead });

  if (!canRead) return null;

  const entries = historyQuery.data ?? [];

  return (
    <Card data-testid="loan-vendor-history-card">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
        Borrower vendor program history
      </h3>

      {historyQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading history…</p>
      )}
      {historyQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load vendor history. Refresh to retry.
        </p>
      )}
      {historyQuery.isSuccess &&
        (entries.length === 0 ? (
          <p className="text-small text-gray-mid m-0">
            No vendor assignment changes recorded on this loan yet.
          </p>
        ) : (
          <ul
            className="m-0 p-0 list-none flex flex-col gap-space-2"
            data-testid="loan-vendor-history-list"
          >
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-space-2 border-b border-border-light pb-space-1"
                data-testid={`loan-vendor-history-row-${entry.id}`}
              >
                <div className="flex flex-col gap-space-1 min-w-0">
                  <span className="flex items-center gap-space-2 flex-wrap">
                    <StatusBadge tone={KIND_TONE[entry.kind] ?? "neutral"}>
                      {KIND_LABEL[entry.kind] ?? entry.kind}
                    </StatusBadge>
                    <span className="text-small font-medium truncate">
                      {/* Resolved server-side from the assignment's
                          CounterpartyId — a removal audits no payload, so a
                          name read out of the audit JSON would be blank on
                          exactly the event staff most want to see. */}
                      {entry.vendorName ?? "Vendor no longer in the registry"}
                    </span>
                    {entry.roleOnLoan && (
                      <span className="text-small text-gray-mid truncate">
                        {entry.roleOnLoan}
                      </span>
                    )}
                  </span>
                  <span className="text-caption text-gray-mid">
                    {entry.summary}
                  </span>
                </div>
                <span className="text-caption text-gray-mid text-right shrink-0">
                  {dateTime(entry.occurredAt)}
                  {entry.actorDisplayName ? (
                    <>
                      <br />
                      {entry.actorDisplayName}
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        ))}
    </Card>
  );
}
